import { T, useGT } from 'gt-react';
import { CrosshairIcon } from '@phosphor-icons/react';
import { reportNumberFormatOptions } from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { defineComponentType } from '../registry';
import { useReportDataset } from '../ReportDataContext';
import { useSeriesColor } from '../palette/ReportColorPaletteContext';
import type { RadarChartComponent } from '../types';
import {
  ColorSwatch,
  ColumnSelect,
  ConfigRow,
  DatasetSelectRow,
  ToggleRow,
  valueByCategoryTitle,
  ChartFrame,
  chartReady,
  useChartFit,
  ConfigSelectTrigger,
  columnLabelOf,
} from './shared';
import { NO_AXIS } from './chartConstants';
import {
  aggregateByCategory,
  topWithOther,
  withNumericValues,
} from './chartRows';
import { BuiltinChart } from './BuiltinChart';
import type { BuiltinChartRequest } from './viz/request';

/** The tooltip writes a section's value as a plain Number. */
const PLAIN_NUMBER = reportNumberFormatOptions(undefined);

function RadarChartRenderer({ component }: { component: RadarChartComponent }) {
  const gt = useGT();
  const otherLabel = gt('Other');
  const { detail } = useChartFit();
  const seriesColor = useSeriesColor();
  const color = component.color ?? seriesColor;
  const dataset = useReportDataset(component.datasetKey);
  const ready = chartReady(dataset, component.subjectKey, component.valueKey);
  const columns = dataset?.columns ?? [];
  const subjectLabel = columnLabelOf(columns, component.subjectKey);
  const valueLabel = columnLabelOf(columns, component.valueKey);

  // Apply maxSections: keep top-N by value, roll the rest into "Other".
  const displayRows =
    ready && dataset
      ? topWithOther(
          // One slice per category: rows naming the same one sum into it.
          aggregateByCategory(
            withNumericValues(dataset.rows, component.valueKey ?? ''),
            component.subjectKey,
            component.valueKey,
          ),
          component.subjectKey ?? '',
          component.valueKey ?? '',
          component.maxSections,
          otherLabel,
        )
      : [];
  const request: BuiltinChartRequest = {
    kind: 'radar',
    options: {
      data: displayRows,
      category: component.subjectKey ?? '',
      value: component.valueKey ?? '',
      categoryTitle: subjectLabel,
      valueTitle: valueLabel,
      grid: component.gridType,
      filled: component.filled,
      color,
      labels: detail.tickLabels,
      valueFormat: PLAIN_NUMBER,
    },
  };

  return (
    <ChartFrame
      ready={ready}
      hasData={dataset !== null}
      icon={<CrosshairIcon className="h-8 w-8 text-muted-foreground/40" />}
    >
      <BuiltinChart request={request} />
    </ChartFrame>
  );
}

function RadarChartConfigForm({
  component,
  onUpdate,
}: {
  component: RadarChartComponent;
  onUpdate: (updated: RadarChartComponent) => void;
}) {
  const gt = useGT();
  const seriesColor = useSeriesColor();
  const dataset = useReportDataset(component.datasetKey);
  const allColumns = dataset?.columns ?? [];
  const numericColumns = allColumns.filter((c) => c.dataType === 'number');

  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({
            ...component,
            datasetKey,
            subjectKey: null,
            valueKey: null,
          })
        }
      />
      <ConfigRow label={gt('Subject axis (categories)')}>
        <ColumnSelect
          value={component.subjectKey}
          columns={allColumns}
          placeholder={gt('Subject column…')}
          onChange={(k) => onUpdate({ ...component, subjectKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={gt('Values')}>
        <ColumnSelect
          value={component.valueKey}
          columns={
            numericColumns.length > 0
              ? numericColumns
              : [{ key: NO_AXIS, label: gt('No numeric columns in result') }]
          }
          placeholder={gt('Value column…')}
          onChange={(k) => onUpdate({ ...component, valueKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={gt('Grid type')} htmlFor="radar-grid-type">
        <Select
          value={component.gridType}
          onValueChange={(v) =>
            onUpdate({
              ...component,
              gridType:
                v === 'circle' ? ('circle' as const) : ('polygon' as const),
            })
          }
        >
          <ConfigSelectTrigger id="radar-grid-type">
            <SelectValue />
          </ConfigSelectTrigger>
          <SelectContent>
            <SelectItem value="polygon">
              <T>Polygon</T>
            </SelectItem>
            <SelectItem value="circle">
              <T>Circle</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </ConfigRow>
      <ConfigRow label={gt('Max sections')} htmlFor="radar-max-sections">
        <Select
          value={String(component.maxSections)}
          onValueChange={(v) =>
            onUpdate({ ...component, maxSections: Number(v) })
          }
        >
          <ConfigSelectTrigger id="radar-max-sections">
            <SelectValue />
          </ConfigSelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              <T>All</T>
            </SelectItem>
            <SelectItem value="5">
              <T>Top 5</T>
            </SelectItem>
            <SelectItem value="8">
              <T>Top 8</T>
            </SelectItem>
            <SelectItem value="10">
              <T>Top 10</T>
            </SelectItem>
            <SelectItem value="15">
              <T>Top 15</T>
            </SelectItem>
            <SelectItem value="20">
              <T>Top 20</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </ConfigRow>
      <div className="flex items-center justify-between">
        <Label>{gt('Color')}</Label>
        <ColorSwatch
          value={component.color ?? seriesColor}
          label={gt('Radar series color')}
          onChange={(next) => onUpdate({ ...component, color: next })}
        />
      </div>
      <ToggleRow
        label={gt('Filled')}
        id="radar-filled"
        checked={component.filled}
        onCheckedChange={(v) => onUpdate({ ...component, filled: v })}
      />
    </div>
  );
}

export function RadarChartPalettePreview() {
  // Hexagon grid: center (22,18) r=13, 6 axes
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        {/* outer hexagon */}
        <polygon
          points="22,5 33.3,11.5 33.3,24.5 22,31 10.7,24.5 10.7,11.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        {/* mid hexagon */}
        <polygon
          points="22,11.5 27.6,14.75 27.6,21.25 22,24.5 16.4,21.25 16.4,14.75"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        {/* axis spokes */}
        <line
          x1="22"
          y1="18"
          x2="22"
          y2="5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        <line
          x1="22"
          y1="18"
          x2="33.3"
          y2="11.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        <line
          x1="22"
          y1="18"
          x2="33.3"
          y2="24.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        <line
          x1="22"
          y1="18"
          x2="22"
          y2="31"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        <line
          x1="22"
          y1="18"
          x2="10.7"
          y2="24.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        <line
          x1="22"
          y1="18"
          x2="10.7"
          y2="11.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeOpacity="0.25"
        />
        {/* data polygon */}
        <polygon
          points="22,7.7 31.1,13 30.5,23.5 22,28 13.5,22.8 14.5,12.5"
          fill="currentColor"
          fillOpacity="0.15"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}

export const radarChartDefinition = defineComponentType<RadarChartComponent>({
  type: 'radar-chart',
  label: 'Radar chart',
  defaultTitle: valueByCategoryTitle('subjectKey', 'valueKey', 'Radar chart'),
  description:
    'Dataset-bound radar/spider chart: one axis per subjectKey value, sized by the summed numeric valueKey column. Best for comparing 3-8 categories.',
  columnRefFields: ['subjectKey', 'valueKey'],
  version: 2,
  migrations: {
    // Radars once stored violet as their color; one still holding it follows the palette.
    1: ({ color, ...config }) =>
      color === '#8b5cf6' ? config : { ...config, color },
  },
  make: (id) => ({
    type: 'radar-chart',
    id,
    subjectKey: null,
    valueKey: null,
    gridType: 'polygon',
    filled: true,
    maxSections: 8,
    // Saturated default so the filled area reads clearly; a light pastel at
    // low fill opacity washes out to near-white. Still user-customizable.
  }),
  Renderer: RadarChartRenderer,
  ConfigForm: RadarChartConfigForm,
  Preview: RadarChartPalettePreview,
});
